import { FormEvent, useEffect, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, Eye, EyeOff, Lock, Mail, UserPlus } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '@/stores/authStore'

// Resolve the bare /health URL from VITE_API_URL (which points at /api/v1).
// Stripping the /api/vX suffix lands on the unauthenticated, un-rate-limited
// process-info endpoint defined in backend/src/index.ts.
const API_BASE = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3001/api/v1'
const HEALTH_URL = API_BASE.replace(/\/api\/v\d+\/?$/, '') + '/health'

const LoginPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { signInWithPassword, status, isLoading } = useAuthStore()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [emailFieldError, setEmailFieldError] = useState('')
  const [passwordFieldError, setPasswordFieldError] = useState('')

  const returnTo = (location.state as { returnTo?: string } | null)?.returnTo || '/dashboard'

  // Fire-and-forget ping to wake the backend while the user is still typing
  // their email. The Docker image cold-starts in 15-25s (ts-node compile +
  // prisma migrate deploy + connection-pool warmup); kicking it off here
  // means /auth/me lands on a warm process. No-op if the request fails.
  useEffect(() => {
    fetch(HEALTH_URL, { method: 'GET', cache: 'no-store', credentials: 'omit' }).catch(() => {})
  }, [])

  if (status === 'authenticated' && !isLoading) {
    return <Navigate to={returnTo} replace />
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    const trimmed = email.trim().toLowerCase()
    const emailErr = trimmed
      ? isValidEmail(trimmed)
        ? ''
        : 'That doesn’t look like a valid email address.'
      : 'Enter your email to continue.'
    const passwordErr = password ? '' : 'Enter your password to continue.'
    setEmailFieldError(emailErr)
    setPasswordFieldError(passwordErr)
    if (emailErr || passwordErr) return

    setSubmitting(true)
    try {
      await signInWithPassword(trimmed, password)
      toast.success('Welcome back')
      navigate(returnTo, { replace: true })
    } catch (err: any) {
      setError(prettifyError(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <AuthShell title="Member sign in" subtitle="Welcome back to Shorewood">
      <form onSubmit={handleSubmit} className="space-y-5">
        <label className="block">
          <span className="block text-sm font-medium text-white mb-2">Email</span>
          <div className="relative">
            <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              autoComplete="email"
              required
              autoFocus
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                if (emailFieldError) setEmailFieldError('')
              }}
              onBlur={() => {
                const trimmed = email.trim()
                if (trimmed && !isValidEmail(trimmed)) {
                  setEmailFieldError('That doesn’t look like a valid email address.')
                }
              }}
              placeholder="you@example.com"
              aria-invalid={emailFieldError ? true : undefined}
              aria-describedby={emailFieldError ? 'login-email-error' : undefined}
              className={`w-full pl-10 pr-4 py-3 bg-white/95 border rounded-lg input-focus text-gray-900 placeholder-gray-400 ${
                emailFieldError ? 'border-rose-400 ring-1 ring-rose-300/40' : 'border-gray-200'
              }`}
            />
          </div>
          {emailFieldError && (
            <p
              id="login-email-error"
              role="alert"
              className="mt-1.5 text-xs text-rose-200"
            >
              {emailFieldError}
            </p>
          )}
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-white mb-2">Password</span>
          <div className="relative">
            <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                if (passwordFieldError) setPasswordFieldError('')
              }}
              placeholder="Your password"
              aria-invalid={passwordFieldError ? true : undefined}
              aria-describedby={passwordFieldError ? 'login-password-error' : undefined}
              className={`w-full pl-10 pr-11 py-3 bg-white/95 border rounded-lg input-focus text-gray-900 placeholder-gray-400 ${
                passwordFieldError ? 'border-rose-400 ring-1 ring-rose-300/40' : 'border-gray-200'
              }`}
            />
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {passwordFieldError && (
            <p
              id="login-password-error"
              role="alert"
              className="mt-1.5 text-xs text-rose-200"
            >
              {passwordFieldError}
            </p>
          )}
          <div className="mt-2 flex justify-end">
            <Link
              to="/forgot-password"
              className="text-xs text-blue-200 hover:text-white transition-colors"
            >
              Forgot your password?
            </Link>
          </div>
        </label>

        {error && (
          <div className="bg-red-500/15 border border-red-300/40 text-red-100 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full bg-white text-blue-900 font-semibold hover:bg-blue-50 transition disabled:opacity-60 shadow-lg"
        >
          {submitting ? 'Signing in…' : 'Login'}
        </button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <div className="flex-1 h-px bg-white/15" />
        <span className="text-xs uppercase tracking-eyebrow text-blue-200/80">or</span>
        <div className="flex-1 h-px bg-white/15" />
      </div>

      <Link
        to="/signup"
        className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 rounded-full border border-white/40 bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors"
      >
        <UserPlus size={18} />
        New Member — Request Access
      </Link>
    </AuthShell>
  )
}

const AuthShell = ({
  children,
  title,
  subtitle,
}: {
  children: React.ReactNode
  title: string
  subtitle: string
}) => (
  <div
    className="min-h-screen w-full flex items-center justify-center p-4 relative overflow-hidden bg-deepwater"
    style={{
      backgroundImage: 'url(/fonts/images/login_background.png)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed',
    }}
  >
    {/* Same three-layer overlay as the LandingPage Hero */}
    <div
      className="absolute inset-0 bg-gradient-to-b from-deepwater/85 via-deepwater/55 to-deepwater/95"
      aria-hidden="true"
    />
    <div
      className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_30%,rgba(201,160,82,0.10),transparent_55%)]"
      aria-hidden="true"
    />
    <div
      className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/40 to-transparent"
      aria-hidden="true"
    />
    <div className="relative z-10 w-full max-w-md">
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-blue-100 hover:text-white text-sm mb-6"
      >
        <ArrowLeft size={14} /> Back
      </Link>
      <div className="text-center mb-8">
        <h1 className="serif-font text-3xl font-bold text-white mb-1">{title}</h1>
        <p className="text-blue-100/80 text-sm">{subtitle}</p>
      </div>
      <div className="glass-effect bg-white/10 border-white/20 rounded-2xl p-7 shadow-2xl">
        {children}
      </div>
    </div>
  </div>
)

const isValidEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)

const prettifyError = (err: any): string => {
  const message: string = err?.message || ''
  if (/Invalid login/i.test(message)) return 'Email or password is incorrect.'
  if (/email not confirmed/i.test(message))
    return 'Please confirm your email — we sent you a link.'
  if (/rate limit/i.test(message))
    return 'Too many attempts. Wait a minute and try again.'
  return message || 'Something went wrong. Please try again.'
}

export default LoginPage
